import {
  createFileRoute,
  useNavigate,
  useParams,
} from "@tanstack/react-router";

export const Route = createFileRoute("/(auth)/reset-password/$token")({
  loader: async ({ params }) => {
    try {
      const { token } = params;
      await authInstance.get(`/api/auth/reset-password/${token}/`);
      return { valid: true };
    } catch (error) {
      return { valid: false, error };
    }
  },
  component: ResetPassword,
});

interface LoaderData {
  valid: boolean;
  error?: AxiosError<{ detail: string }>;
}

import { CustomIcon } from "@/components/crayons-code-logo/CustomIcon.main";
import { CustomVerticalIcon } from "@/components/crayons-code-logo/CustomIcon.vertical";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Eye, EyeOff } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { toast } from "sonner";
import { authInstance } from "@/lib/axios";
import type { AxiosError } from "axios";
import { applyServerErrors } from "@/lib/apply-server-errors";
import {
  resetPasswordSchema,
  type ResetPasswordFormValues,
  RESET_PASSWORD_FIELDS,
} from "@/schemas/auth";

function ResetPassword() {
  const { token } = useParams({ strict: false });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const loaderData: LoaderData = Route.useLoaderData();

  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<ResetPasswordFormValues>({
    resolver: zodResolver(resetPasswordSchema),
    defaultValues: { new_password: "", confirm_password: "" },
  });

  const onValid = async (data: ResetPasswordFormValues) => {
    setIsSubmitting(true);
    try {
      await authInstance.post(`/api/auth/reset-password/${token}/`, {
        new_password: data.new_password,
      });
      toast.success("Your password has been reset successfully!");

      // Redirect to login or dashboard after successful reset
      setTimeout(() => {
        navigate({ to: "/login" });
      }, 600);
    } catch (err) {
      const { handled, rest } = applyServerErrors(
        err,
        setError,
        RESET_PASSWORD_FIELDS,
      );
      if (!handled || rest.length > 0)
        toast.error(rest[0] ?? "Failed to reset password. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return loaderData.valid ? (
    <div className="grid min-h-svh bg-muted">
      {/* Left side - Form */}

      <div className="flex flex-col gap-4 p-6 md:p-10">
        {/* Form Container */}
        <div className="flex flex-1 flex-col gap-6 items-center justify-center">
          <h1>
            <div className="flex items-center justify-center gap-2 font-medium text-xl pb-2">
              <CustomVerticalIcon size={180} />
            </div>
          </h1>

          <Card className="w-full max-w-sm text-center shadow-2xl">
            <CardHeader>
              <CardTitle className="text-2xl font-bold">
                Reset Your Password
              </CardTitle>
              <CardDescription>
                Create a new password for your account
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit(onValid)} className="space-y-4">
                {/* New Password Field */}
                <div className="grid gap-2 text-left">
                  <Label htmlFor="new-password">New Password</Label>
                  <div className="relative">
                    <Input
                      id="new-password"
                      type={showPassword ? "text" : "password"}
                      placeholder="******"
                      autoComplete="new-password"
                      {...register("new_password")}
                      className={`pr-10 ${
                        errors.new_password
                          ? "border-red-500 focus:border-red-500"
                          : ""
                      }`}
                    />
                    <button
                      type="button"
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? (
                        <Eye className="h-4 w-4" />
                      ) : (
                        <EyeOff className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                  {errors.new_password ? (
                    <p className="text-xs text-red-500">{errors.new_password.message}</p>
                  ) : (
                    <p className="text-xs text-muted-foreground">
                      Must be at least 8 characters
                    </p>
                  )}
                </div>

                {/* Confirm Password Field */}
                <div className="grid gap-2 text-left">
                  <Label htmlFor="confirm-password">Confirm Password</Label>
                  <div className="relative">
                    <Input
                      id="confirm-password"
                      type={showConfirmPassword ? "text" : "password"}
                      placeholder="******"
                      autoComplete="new-password"
                      {...register("confirm_password")}
                      className={`pr-10 ${
                        errors.confirm_password
                          ? "border-red-500 focus:border-red-500"
                          : ""
                      }`}
                    />
                    <button
                      type="button"
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      onClick={() =>
                        setShowConfirmPassword(!showConfirmPassword)
                      }
                    >
                      {showConfirmPassword ? (
                        <Eye className="h-4 w-4" />
                      ) : (
                        <EyeOff className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                  {errors.confirm_password && (
                    <p className="text-xs text-red-500">
                      {errors.confirm_password.message}
                    </p>
                  )}
                </div>

                <Button
                  type="submit"
                  className="w-full mt-2"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Resetting..." : "Reset Password"}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  ) : (
    <div className="h-svh">
      <div className="m-auto flex h-full w-full flex-col items-center justify-center gap-2">
        <div className="flex items-center justify-center gap-2 font-medium text-xl pb-2">
          <CustomIcon size={280} />
        </div>
        <h1 className="text-[7rem] leading-tight font-bold text-primary">
          404
        </h1>
        <span className="font-medium">Not Found</span>
        <p className="text-muted-foreground text-center">
          {loaderData?.error?.response?.data?.detail}
        </p>
        <div className="mt-6 flex gap-4">
          <Button variant="outline" onClick={() => history.go(-1)}>
            Go Back
          </Button>
          <Button onClick={() => navigate({ to: "/" })}>Back to Home</Button>
        </div>
      </div>
    </div>
  );
}
